/**
 * Field sources and copy-on-write inheritance for item content (PD-16509,
 * docs/adr/0004-counterparts-link-to-master-item.md).
 *
 * Each content field on each item records where its value comes from:
 * - `erp` (the default, stored as a MISSING key): the item's ERP owns it and
 *   every ERP sync may overwrite it.
 * - `workd`: someone edited it in Workd, so ERP syncs leave it alone.
 * - `inherit`: it is copied from the item's parent (its master item or product)
 *   whenever the parent changes.
 *
 * Inherited values are COPIED into the child's own columns rather than resolved
 * at read time, so every raw reader (report SQL, the list builder, SM1, the
 * legacy server, mover-integration order entry) keeps working unchanged. The
 * ERP shadow (`item.erpContent`) holds the ERP's latest value of every content
 * field, so unlinking or "Sync from ERP" restores ERP values immediately
 * instead of waiting for an ERP record that may never change again.
 *
 * mover-integration implements the same contract on the import side: it always
 * writes `erpContent` and only writes live columns the ERP still owns.
 *
 * Everything here is pure so the rules are testable without a database.
 */

/** Descriptive fields that may be shared through a master item or product. */
export const ITEM_CONTENT_FIELDS = [
	'description',
	'extendedDescription',
	'manufacturer',
	'brandEquivalent',
	'category',
	'subcategory',
	'family',
	'tradeStyle',
	'storageType',
	'countryOfOrigin',
	'specifications',
] as const;

export type ItemContentField = (typeof ITEM_CONTENT_FIELDS)[number];

/** Custom fields are sourced per key, as `customFields.<key>`. */
export const CUSTOM_FIELD_SOURCE_PREFIX = 'customFields.';

/** The item's images as a whole. `inherit` adds the parent's images to the item. */
export const ITEM_IMAGES_SOURCE_KEY = 'images';

/** The item's attached files as a whole, same rule as images. */
export const ITEM_FILES_SOURCE_KEY = 'files';

/** What is stored in `item.fieldSources`. A missing key means `erp`. */
export type StoredItemFieldSource = 'workd' | 'inherit';

export type ItemFieldSource = 'erp' | StoredItemFieldSource;

/** `item.fieldSources`: source key → stored source. */
export type ItemFieldSourcesModel = Record<string, StoredItemFieldSource>;

/** `item.erpContent`: the ERP's latest value of every content field it supplied. */
export type ItemErpContentModel = Partial<
	Record<ItemContentField, string | null>
> & {
	customFields?: Record<string, unknown> | null;
};

/** The content values of one item, as read from its columns. */
export type ItemContentValuesModel = Partial<
	Record<ItemContentField, string | null>
> & {
	customFields?: Record<string, unknown> | null;
};

/** How a child relates to the parent it is linked to. */
export type ItemLinkRelation = 'counterpart' | 'variant';

const CONTENT_FIELD_SET: ReadonlySet<string> = new Set(ITEM_CONTENT_FIELDS);

export const isItemContentField = (key: string): key is ItemContentField =>
	CONTENT_FIELD_SET.has(key);

export const customFieldSourceKey = (customFieldKey: string): string =>
	`${CUSTOM_FIELD_SOURCE_PREFIX}${customFieldKey}`;

/** The custom-field key a `customFields.<key>` source key names, or null. */
export const parseCustomFieldSourceKey = (sourceKey: string): string | null =>
	sourceKey.startsWith(CUSTOM_FIELD_SOURCE_PREFIX) &&
	sourceKey.length > CUSTOM_FIELD_SOURCE_PREFIX.length
		? sourceKey.slice(CUSTOM_FIELD_SOURCE_PREFIX.length)
		: null;

/** A key a field source may be recorded under. */
export const isItemFieldSourceKey = (key: string): boolean =>
	isItemContentField(key) ||
	key === ITEM_IMAGES_SOURCE_KEY ||
	key === ITEM_FILES_SOURCE_KEY ||
	parseCustomFieldSourceKey(key) != null;

const isStoredSource = (value: unknown): value is StoredItemFieldSource =>
	value === 'workd' || value === 'inherit';

/**
 * Normalize whatever the JSON column holds into a clean map. Unknown keys and
 * values are dropped rather than trusted, so a malformed blob can never make a
 * field look Workd-owned and silently stop ERP syncs.
 */
export const normalizeItemFieldSources = (
	raw: unknown,
): ItemFieldSourcesModel => {
	let value = raw;
	if (typeof value === 'string') {
		try {
			value = JSON.parse(value) as unknown;
		} catch {
			return {};
		}
	}
	if (value == null || typeof value !== 'object' || Array.isArray(value)) {
		return {};
	}
	const result: ItemFieldSourcesModel = {};
	for (const [key, source] of Object.entries(value)) {
		if (isItemFieldSourceKey(key) && isStoredSource(source)) {
			result[key] = source;
		}
	}
	return result;
};

export const getItemFieldSource = (
	sources: ItemFieldSourcesModel | null | undefined,
	key: string,
): ItemFieldSource => sources?.[key] ?? 'erp';

/**
 * Return a copy of `sources` with `key` set to `source`. `erp` removes the key.
 * Returns null when nothing is left, so the column stays NULL for every item
 * that was never touched (the overwhelming majority).
 */
export const withItemFieldSource = (
	sources: ItemFieldSourcesModel | null | undefined,
	key: string,
	source: ItemFieldSource,
): ItemFieldSourcesModel | null =>
	withItemFieldSources(sources, [key], source);

export const withItemFieldSources = (
	sources: ItemFieldSourcesModel | null | undefined,
	keys: ReadonlyArray<string>,
	source: ItemFieldSource,
): ItemFieldSourcesModel | null => {
	const next: ItemFieldSourcesModel = { ...(sources ?? {}) };
	for (const key of keys) {
		if (source === 'erp') {
			delete next[key];
		} else {
			next[key] = source;
		}
	}
	return Object.keys(next).length === 0 ? null : next;
};

export const listItemFieldSourceKeys = (
	sources: ItemFieldSourcesModel | null | undefined,
	source: StoredItemFieldSource,
): Array<string> =>
	Object.entries(sources ?? {})
		.filter(([, value]) => value === source)
		.map(([key]) => key);

const sameValue = (a: unknown, b: unknown): boolean =>
	JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const toObject = (
	value: Record<string, unknown> | null | undefined,
): Record<string, unknown> => ({ ...(value ?? {}) });

/**
 * The column changes that bring a child's inherited fields in line with its
 * parent's current values. Only keys whose source is `inherit` are touched, and
 * only when the value actually differs, so an unchanged parent produces an
 * empty patch (and therefore no save, no audit row and no reindex).
 *
 * Custom fields merge per key: an inherited key takes the parent's value, and
 * is removed from the child when the parent has none.
 */
export const planInheritedContentPatch = (
	child: ItemContentValuesModel,
	childSources: ItemFieldSourcesModel | null | undefined,
	parent: ItemContentValuesModel,
): ItemContentValuesModel => {
	const patch: ItemContentValuesModel = {};
	const inherited = listItemFieldSourceKeys(childSources, 'inherit');

	const customFields = toObject(child.customFields);
	let customFieldsChanged = false;

	for (const key of inherited) {
		if (isItemContentField(key)) {
			const parentValue = parent[key] ?? null;
			if (!sameValue(child[key], parentValue)) {
				patch[key] = parentValue;
			}
			continue;
		}

		const customFieldKey = parseCustomFieldSourceKey(key);
		if (customFieldKey == null) {
			continue;
		}
		const parentValue = parent.customFields?.[customFieldKey];
		if (parentValue === undefined || parentValue === null) {
			if (customFieldKey in customFields) {
				delete customFields[customFieldKey];
				customFieldsChanged = true;
			}
		} else if (!sameValue(customFields[customFieldKey], parentValue)) {
			customFields[customFieldKey] = parentValue;
			customFieldsChanged = true;
		}
	}

	if (customFieldsChanged) {
		patch.customFields =
			Object.keys(customFields).length === 0 ? null : customFields;
	}

	return patch;
};

/**
 * The column changes that put the ERP's values back for `keys` (unlink, or
 * "Sync from ERP"). A field the ERP shadow has never recorded is left as it is:
 * the item keeps its current value until the next ERP sync supplies one. That
 * is the safe choice for items created in Workd, which have no ERP at all.
 */
export const planErpRestorePatch = (
	child: ItemContentValuesModel,
	erpContent: ItemErpContentModel | null | undefined,
	keys: ReadonlyArray<string>,
): ItemContentValuesModel => {
	const patch: ItemContentValuesModel = {};
	if (erpContent == null) {
		return patch;
	}

	const customFields = toObject(child.customFields);
	let customFieldsChanged = false;

	for (const key of keys) {
		if (isItemContentField(key)) {
			if (key in erpContent) {
				const erpValue = erpContent[key] ?? null;
				if (!sameValue(child[key], erpValue)) {
					patch[key] = erpValue;
				}
			}
			continue;
		}

		const customFieldKey = parseCustomFieldSourceKey(key);
		if (customFieldKey == null || erpContent.customFields == null) {
			continue;
		}
		const erpValue = erpContent.customFields[customFieldKey];
		if (erpValue === undefined || erpValue === null) {
			if (customFieldKey in customFields) {
				delete customFields[customFieldKey];
				customFieldsChanged = true;
			}
		} else if (!sameValue(customFields[customFieldKey], erpValue)) {
			customFields[customFieldKey] = erpValue;
			customFieldsChanged = true;
		}
	}

	if (customFieldsChanged) {
		patch.customFields =
			Object.keys(customFields).length === 0 ? null : customFields;
	}

	return patch;
};

// An editor form sends '' for an empty field the database holds as NULL, and
// may trim what it sends. Neither is an edit, and treating it as one would
// silently stop that field following its ERP.
const sameEditedValue = (a: unknown, b: unknown): boolean => {
	const normalize = (value: unknown): unknown =>
		typeof value === 'string'
			? value.trim() === ''
				? null
				: value.trim()
			: (value ?? null);
	return sameValue(normalize(a), normalize(b));
};

/**
 * Content keys whose value changed between `before` and `after`. Used to mark
 * fields a user edited in Workd as Workd-owned.
 */
export const listChangedContentKeys = (
	before: ItemContentValuesModel,
	after: ItemContentValuesModel,
): Array<string> => {
	const changed: Array<string> = [];
	for (const field of ITEM_CONTENT_FIELDS) {
		if (field in after && !sameEditedValue(before[field], after[field])) {
			changed.push(field);
		}
	}
	if (after.customFields !== undefined) {
		const beforeFields = before.customFields ?? {};
		const afterFields = after.customFields ?? {};
		const keys = new Set([
			...Object.keys(beforeFields),
			...Object.keys(afterFields),
		]);
		for (const key of keys) {
			if (!sameEditedValue(beforeFields[key], afterFields[key])) {
				changed.push(customFieldSourceKey(key));
			}
		}
	}
	return changed;
};

const isBlank = (value: unknown): boolean =>
	value == null || (typeof value === 'string' && value.trim() === '');

/** Every source key the given items carry a value for, in a stable order. */
export const listPresentSourceKeys = (
	items: ReadonlyArray<ItemContentValuesModel>,
): Array<string> => {
	const keys: Array<string> = [];
	for (const field of ITEM_CONTENT_FIELDS) {
		if (items.some((item) => !isBlank(item[field]))) {
			keys.push(field);
		}
	}
	const customKeys = new Set<string>();
	for (const item of items) {
		for (const [key, value] of Object.entries(item.customFields ?? {})) {
			if (!isBlank(value)) {
				customKeys.add(key);
			}
		}
	}
	for (const key of [...customKeys].sort()) {
		keys.push(customFieldSourceKey(key));
	}
	return keys;
};

const readSourceKey = (
	item: ItemContentValuesModel,
	key: string,
): unknown => {
	if (isItemContentField(key)) {
		return item[key];
	}
	const customFieldKey = parseCustomFieldSourceKey(key);
	return customFieldKey == null
		? undefined
		: item.customFields?.[customFieldKey];
};

const normalizeForCompare = (value: unknown): string =>
	typeof value === 'string'
		? value.trim().replace(/\s+/g, ' ').toLowerCase()
		: JSON.stringify(value ?? null);

/**
 * Source keys where the linked items disagree (ignoring blanks, case and
 * spacing). The link flow asks the user to choose a value only for these; every
 * other field seeds itself from the one value the items share.
 */
export const listDifferingSourceKeys = (
	items: ReadonlyArray<ItemContentValuesModel>,
): Array<string> =>
	listPresentSourceKeys(items).filter((key) => {
		const distinct = new Set(
			items
				.map((item) => readSourceKey(item, key))
				.filter((value) => !isBlank(value))
				.map(normalizeForCompare),
		);
		return distinct.size > 1;
	});

/**
 * The content a new master item or product starts with, seeded field by field
 * from the items being linked. `choices` maps a source key to the index (in
 * `items`) of the item whose value the user picked; any other key takes the
 * first non-blank value. `images`/`files` are not content values and are
 * seeded separately.
 */
export const planParentSeed = (
	items: ReadonlyArray<ItemContentValuesModel>,
	choices: Readonly<Record<string, number>>,
): ItemContentValuesModel => {
	const seed: ItemContentValuesModel = {};
	const customFields: Record<string, unknown> = {};

	for (const key of listPresentSourceKeys(items)) {
		const chosenIndex = choices[key];
		const chosen =
			chosenIndex != null && items[chosenIndex] != null
				? readSourceKey(items[chosenIndex], key)
				: undefined;
		const value = !isBlank(chosen)
			? chosen
			: items
					.map((item) => readSourceKey(item, key))
					.find((candidate) => !isBlank(candidate));
		if (value === undefined) {
			continue;
		}
		if (isItemContentField(key)) {
			seed[key] = typeof value === 'string' ? value : String(value);
		} else {
			const customFieldKey = parseCustomFieldSourceKey(key);
			if (customFieldKey != null) {
				customFields[customFieldKey] = value;
			}
		}
	}

	if (Object.keys(customFields).length > 0) {
		seed.customFields = customFields;
	}
	return seed;
};

/**
 * The keys that inherit by default when an item is linked. Counterparts are the
 * same sellable thing, so everything is shared. Variants differ by exactly the
 * things a customer chooses between, so their own description, pack
 * specification and images stay with the variant unless the user ticks them.
 */
export const defaultInheritedSourceKeys = (
	relation: ItemLinkRelation,
	parent: ItemContentValuesModel,
): Array<string> => {
	const keys: Array<string> = [];
	for (const field of ITEM_CONTENT_FIELDS) {
		if (
			relation === 'variant' &&
			(field === 'description' || field === 'specifications')
		) {
			continue;
		}
		keys.push(field);
	}
	for (const key of Object.keys(parent.customFields ?? {}).sort()) {
		keys.push(customFieldSourceKey(key));
	}
	if (relation === 'counterpart') {
		keys.push(ITEM_IMAGES_SOURCE_KEY);
	}
	keys.push(ITEM_FILES_SOURCE_KEY);
	return keys;
};
